import { useEffect, useState } from "react";
import { t } from "ttag";

import * as Errors from "metabase/lib/errors";
import { Box, Button, Modal, Stack, Text, TextInput } from "metabase/ui";

interface OTPVerificationProps {
  username: string;
  password: string;
  onLoginSuccess: () => void;
  onBack: () => void;
}

interface LoginResponse {
  id?: string;
  error?: string;
  message?: string;
}

export const OTPVerification = ({
  username,
  password,
  onLoginSuccess,
  onBack,
}: OTPVerificationProps): JSX.Element | null => {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [canResend, setCanResend] = useState(true);

  // Countdown for resend button
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(
        () => setResendCountdown(resendCountdown - 1),
        1000,
      );
      return () => clearTimeout(timer);
    } else if (resendCountdown === 0 && !canResend) {
      setCanResend(true);
    }
  }, [resendCountdown, canResend]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim()) {
      setError(Errors.required);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username,
          password,
          otp: otp.trim(),
        }),
      });

      const data: LoginResponse = await response.json();

      if (!response.ok) {
        if (data.error === "otp-required") {
          // Still need OTP - should not happen but handle gracefully
          setError(data.message || t`OTP is required`);
        } else {
          setError(data.message || t`Invalid OTP code`);
        }
        return;
      }

      // Check if we have a session ID in the response
      if (data.id) {
        onLoginSuccess();
      }
    } catch (err) {
      setError(t`An error occurred. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/session/request-otp", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.message || t`Failed to send OTP`);
        return;
      }

      // Start 60 second countdown
      setCanResend(false);
      setResendCountdown(60);
    } catch (err) {
      setError(t`An error occurred. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal opened={true} onClose={onBack} title={t`Verify your device`}>
      <Box p="lg">
        <Stack gap="md">
          <Box ta="center" c="text-light">
            {t`For your security, we need to verify this device. Please check your email for a verification code.`}
          </Box>

          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <TextInput
                label={t`Verification code`}
                placeholder="000000"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                maxLength={6}
                autoFocus
                styles={{
                  input: {
                    textAlign: "center",
                    fontSize: "24px",
                    letterSpacing: "0.5em",
                    fontWeight: "bold",
                  },
                }}
              />
              {error && (
                <Text c="error" role="alert" aria-label={error}>
                  {error}
                </Text>
              )}
              <Button
                type="submit"
                fullWidth
                disabled={isLoading || otp.length !== 6}
                loading={isLoading}
              >
                {t`Verify`}
              </Button>
              <Button
                type="button"
                variant="subtle"
                fullWidth
                onClick={handleResendOTP}
                disabled={!canResend || isLoading}
              >
                {canResend
                  ? t`Resend code`
                  : t`Resend code (${resendCountdown}s)`}
              </Button>
              <Button
                type="button"
                variant="subtle"
                fullWidth
                color="gray"
                onClick={onBack}
                disabled={isLoading}
              >
                {t`Back to login`}
              </Button>
            </Stack>
          </form>
        </Stack>
      </Box>
    </Modal>
  );
};
